/**
 * Convenção de nomes em `clientes` (CRM + e-com) — sem coluna `nome_fantasia`:
 * - `nome`          → nome fantasia (PJ) ou nome da pessoa (PF)
 * - `razaosocial`   → razão social (PJ); null/vazio em PF
 *
 * Em formulários, `nome_fantasia` / `razao_social` são só aliases de UI e devem
 * ser mapeados para `nome` / `razaosocial` antes do save (ver AddClientForm).
 *
 * Persistência (AddClientForm / ecomAuth): sem fantasia, `nome` espelha a razão social.
 *
 * Display:
 * - comercial (listas, chat, busca): fantasia → razão
 * - jurídico / duplicatas: razão → fantasia
 */

function norm(value) {
  return String(value ?? "").trim();
}

function digitsOnly(value) {
  return String(value ?? "").replace(/\D+/g, "");
}

export function getRazaoSocial(client) {
  if (!client || typeof client !== "object") return "";
  return norm(
    client.razaosocial ||
      client.razao_social ||
      client.companyName ||
      client.company_name
  );
}

/** Lê a fantasia da coluna `clientes.nome` (aliases de API/UI: name, tradeName). */
export function getNomeFantasia(client) {
  if (!client || typeof client !== "object") return "";
  return norm(client.nome || client.name || client.tradeName || client.trade_name);
}

/**
 * Detecta PJ com tolerância a `tipo` CRM (0=PF, 1=PJ) vs e-com (1=PF, 2=PJ).
 *
 * @param {object} client
 * @param {{ allowEcomTipoAmbiguity?: boolean }} [options]
 *   Quando `allowEcomTipoAmbiguity` (ex.: painel do chat), `tipo===1` + CPF sem
 *   CNPJ/razão é tratado como PF (e-com). No CRM puro, `tipo===1` é sempre PJ.
 */
export function isPessoaJuridica(client, options = {}) {
  if (!client || typeof client !== "object") return false;

  const cnpjDigits = digitsOnly(client.cnpj);
  if (cnpjDigits.length >= 14) return true;

  const razao = getRazaoSocial(client);
  const tipoRaw = client.tipo ?? client.clientType ?? client.client_type;
  const hasTipo = tipoRaw != null && tipoRaw !== "";
  const tipo = hasTipo ? Number(tipoRaw) : NaN;

  if (tipo === 2) return true; // e-com PJ
  if (hasTipo && tipo === 0) return false; // CRM PF

  if (hasTipo && tipo === 1) {
    if (options.allowEcomTipoAmbiguity === true) {
      // Ambíguo: CRM PJ (=1) vs e-com PF (=1)
      const cpfDigits = digitsOnly(client.cpf);
      if (cpfDigits.length === 11 && cnpjDigits.length < 14 && !razao) return false;
    }
    return true; // CRM: tipo 1 = PJ
  }

  if (razao) return true;

  const tipoStr = String(tipoRaw || "").toLowerCase();
  if (tipoStr.includes("jur")) return true;

  return false;
}

/** Título comercial: fantasia, com fallback para razão social em PJ. */
export function getClientCommercialName(client) {
  const fantasia = getNomeFantasia(client);
  if (!isPessoaJuridica(client)) return fantasia;
  return fantasia || getRazaoSocial(client);
}

/** Nome legal / identidade: razão social, com fallback para fantasia em PJ. */
export function getClientLegalName(client) {
  const fantasia = getNomeFantasia(client);
  if (!isPessoaJuridica(client)) return fantasia;
  return getRazaoSocial(client) || fantasia;
}
